import { Card, Flex, theme as antdTheme, Typography } from 'antd'
import type { ResolvedBoardColumn } from 'features/libra/dashboard/ticketDashboard/filtering/resolveBoardColumns'
import { resolveToneColor } from 'features/libra/dashboard/ticketDashboard/pillTone'
import type { PillCategories } from 'features/libra/dashboard/ticketDashboard/types'
import type { FC } from 'react'
import { memo } from 'react'
import styled from 'styled-components'

import { VirtualTicketList } from './VirtualTicketList'

/**
 * Full column height, with the body left to the card list. The header carries
 * the column's tone as a top stripe over a faint tint, so columns read apart.
 */
const ColumnCard = styled(Card)<{ $accent: string }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-top: 0.1rem solid ${({ $accent }) => $accent};

  .ant-card-head {
    background: color-mix(
      in srgb,
      ${({ $accent }) => $accent} 12%,
      transparent
    );
  }

  .ant-card-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }
`

interface TicketBoardColumnProps {
  column: ResolvedBoardColumn
  categories: PillCategories
  onSelect: (cardId: string) => void
}

const RawTicketBoardColumn: FC<TicketBoardColumnProps> = ({
  column,
  categories,
  onSelect,
}) => {
  const { token } = antdTheme.useToken()
  const accent = resolveToneColor(column.tone, token) ?? token.colorBorder

  return (
    <ColumnCard
      size='small'
      $accent={accent}
      title={
        <Flex justify='space-between' align='center' gap='small'>
          <Typography.Text strong ellipsis>
            {column.label}
          </Typography.Text>
          <Typography.Text type='secondary'>
            {column.cards.length}
          </Typography.Text>
        </Flex>
      }>
      <VirtualTicketList
        cards={column.cards}
        categories={categories}
        onSelect={onSelect}
      />
    </ColumnCard>
  )
}

export const TicketBoardColumn = memo(RawTicketBoardColumn)
